/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */

import { useCallback } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import styled from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { H4 } from 'foundations-components/transitional/components/H4';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import UsageTracker from '../../../utils/UsageTracker';
import { calculateExamDurationSeconds } from '../../../utils/ContentUtils';
import { getHumanizedDuration } from '../../../utils/utils';
import { useFullTrack } from '../../tracks';
import { useStartExam } from '../examsQueries';
import { useExamRulesState } from '../examsStores';
import { Metrics } from '../../../Metrics';
import { BaseContentControlWrapper } from '../../../components/ContentControlWrapper';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PaddedContainer = styled.div.withConfig({
  displayName: "ExamRulesScreen__PaddedContainer",
  componentId: "sc-17s80es-0"
})(["padding:10% 5%;"]);
const ExamRulesScreen = ({
  cooldownHourCount = 12,
  trackId,
  language,
  onExamStart
}) => {
  const {
    isDesktop
  } = useMediaQuery();
  const {
    track
  } = useFullTrack(trackId, language);
  const {
    setShowExamRules
  } = useExamRulesState();
  const [startExam, {
    startExamLoading,
    startExamError
  }] = useStartExam();
  const requiredCorrectQuestionCount = track && track.exam ? track.exam.passThreshold : 0;
  const questionCount = track && track.exam ? track.exam.length : 0;
  const durationMinutes = track && track.exam ? track.exam.durationMinutes : 0;
  const handleStart = useCallback(() => {
    if (!track || !track.exam) {
      return null;
    }
    return startExam(Number(trackId), track.exam.id, track.exam.language).then(attempt => {
      setShowExamRules(false);
      onExamStart === null || onExamStart === void 0 || onExamStart();
      UsageTracker.track('examInteraction', {
        action: 'start',
        examId: track && track.exam.id,
        examLength: attempt && attempt.questions.length,
        trackId: trackId && trackId.toString(),
        trackName: track.name,
        examAttemptId: attempt && attempt.id && attempt.id.toString(),
        language: track.exam.language
      });
      Metrics.counter('exam-started', {
        language: track.exam.language
      }).increment();
    });
  }, [onExamStart, setShowExamRules, startExam, track, trackId]);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(View, {
      backgroundColor: "fill-surface-recessed",
      children: /*#__PURE__*/_jsx(PaddedContainer, {
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          justifyContent: "center",
          children: [isDesktop && /*#__PURE__*/_jsx(Flex, {
            alignSelf: "start",
            marginBlockStart: "500",
            marginInlineEnd: "800",
            children: /*#__PURE__*/_jsx(Illustration, {
              name: "nps-question",
              width: 150
            })
          }), /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.exams.rules.heading"
              })
            }), /*#__PURE__*/_jsxs("div", {
              children: [/*#__PURE__*/_jsxs(List, {
                styled: true,
                children: [/*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.questionCount",
                    options: {
                      questionCount
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.duration",
                    options: {
                      expectedDuration: getHumanizedDuration(calculateExamDurationSeconds(questionCount))
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.requiredCorrectQuestionsCount",
                    options: {
                      requiredCorrectQuestionCount
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.cooldownHourDurationCount",
                    options: {
                      cooldownHourCount
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.maximumDuration",
                    options: {
                      maximumDuration: getHumanizedDuration(durationMinutes, 'minutes')
                    }
                  })
                }), /*#__PURE__*/_jsx(ListItem, {
                  marginBlockEnd: "300",
                  children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
                    message: "learningCenter.exams.rules.warningNoBackNavigation"
                  })
                })]
              }), /*#__PURE__*/_jsx("span", {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.exams.rules.footer"
                })
              })]
            })]
          })]
        })
      })
    }), /*#__PURE__*/_jsx(BaseContentControlWrapper, {
      children: /*#__PURE__*/_jsx(Flex, {
        alignItems: "center",
        justifyContent: "end",
        children: /*#__PURE__*/_jsx(LoadingButton, {
          "data-test-id": "exam-start-button",
          failed: !!startExamError,
          loading: startExamLoading,
          onClick: handleStart,
          use: "primary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.exams.rules.startExam"
          })
        })
      })
    })]
  });
};
export default ExamRulesScreen;